import type { LayerKey, Severity } from './types'
import type { Tier } from '@/analysis/wsTypes'

// ---------- Type guards ----------

export const isSeverity = (v: unknown): v is Severity =>
  v === 0 || v === 1 || v === 2

export const asSeverity = (v: unknown): Severity | null =>
  isSeverity(v) ? v : null

export const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v)

export const asFiniteNumber = (v: unknown): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

// ---------- Formatters ----------

export const fmtMs = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${Math.round(v)}ms`

/** Demux→Dec lag: one decimal below 10ms so sub-ms values are visible. */
export const fmtLagMs = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—'
    : v < 10 ? `${v.toFixed(1)}ms`
    : `${Math.round(v)}ms`

export const fmtKbps = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${Math.round(v)}kbps`

/**
 * Demux In Rate — `demuxIn` counted over the tick's `windowMs`.
 *
 * Deliberately NOT labelled "fps": what `demuxIn` counts is platform-dependent.
 * On Millicast it tracks video frames exactly (demuxIn == demuxOutVid == 30),
 * but on FLV it counts demuxer input *tags* — audio + video + script — running
 * ~2.6x the video frame count (measured: demuxIn 30–59 against demuxOutVid
 * 12–23 in the same tick). Calling that "fps" would misstate it by more than a
 * factor of two.
 *
 * The unit stays neutral; the "Demux rate" column label carries the meaning.
 * (`demuxOutVidRate` is the actual video frame rate — see the fps metric.)
 */
export const fmtRate = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : `${Number(v).toFixed(1)}/s`

export const fmtNumber = (v: number | null | undefined, suffix = ''): string =>
  v == null || !Number.isFinite(v) ? '—' : `${Math.round(v)}${suffix}`

/**
 * Render in/out is a ratio (out ÷ in), shown as a decimal — 1.00 means every
 * demuxed frame reached the screen. Deliberately not a percentage: the spec
 * bands (§3.2.E, 0.95 / 0.80) are stated as ratios, so showing "95%" forces a
 * mental conversion every time it is compared to a threshold.
 */
export const fmtRatio = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '—' : v.toFixed(2)

export const fmtTimestamp = (v: number | null | undefined): string => {
  if (v == null || !Number.isFinite(v)) return '—'
  return new Date(v).toLocaleTimeString('en-GB')
}

/**
 * The three §3.2.E states in words — GREEN "stable @ source FPS", YELLOW
 * "slight fluctuation", RED "dropping to 0" — shortened to fit a hex layer and
 * a table cell (`Unsteady` replaces the longer "Fluctuations").
 *
 * One vocabulary for every surface: a second, near-identical set of words is
 * how the hex and the tables drifted apart in the first place.
 */
export const getDemuxInRateText = (tier: Tier | null | undefined): string => {
  switch (tier) {
    case 2:    return 'Stable'
    case 1:    return 'Unsteady'
    case 0:    return 'Dropping'
    default:   return '—'
  }
}

/**
 * Demux rate as state + raw value, e.g. `Stable · 30.0 fps`.
 *
 * The state alone hides the number that produced it (a "Stable" badge is
 * useless for spotting a stream locked at half the source rate); the number
 * alone makes the reader apply the CV bands in their head. Showing both is the
 * only form that can be checked against the raw telemetry.
 */
export const fmtDemuxRate = (
  v: number | null | undefined,
  tier: Tier | null | undefined,
): string => {
  const value = v == null || !Number.isFinite(Number(v)) ? null : fmtRate(Number(v))
  const state = tier == null ? null : getDemuxInRateText(tier)
  if (state && value) return `${state} · ${value}`
  return value ?? state ?? '—'
}

/** Format a layer value using the conventions for that metric. */
export const formatLayerValue = (key: LayerKey, value: number | null | undefined, tier: Tier | null): string => {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  switch (key) {
    case 'demuxInRate':        return fmtDemuxRate(Number(value), tier)
    case 'demuxToDecodeLagMs': return fmtLagMs(Number(value))
    case 'downloadKbps':       return fmtKbps(Number(value))
    case 'renderInOutGap':     return fmtRatio(Number(value))
  }
}

// ---------- Stats ----------

/** p95 over finite values; null if no finite values. */
export const p95 = (vals: ReadonlyArray<number>): number | null => {
  const xs = vals.filter(Number.isFinite).slice().sort((a, b) => a - b)
  if (!xs.length) return null
  const idx = Math.min(xs.length - 1, Math.max(0, Math.ceil(xs.length * 0.95) - 1))
  return xs[idx]!
}

/** p95 of a numeric field across a list of objects. */
export const p95Of = <T,>(list: ReadonlyArray<T>, pick: (x: T) => unknown): number | null => {
  const nums: number[] = []
  for (const x of list) {
    const n = Number(pick(x))
    if (Number.isFinite(n)) nums.push(n)
  }
  return p95(nums)
}

/**
 * Worst-case severity across a list. Returns null when no data is present
 * (rather than defaulting to "critical" or "healthy", which would mis-color
 * empty groups).
 */
export const worstSeverity = (vals: ReadonlyArray<unknown>): Severity | null => {
  let worst: Severity | null = null
  for (const v of vals) {
    if (isSeverity(v) && (worst === null || v < worst)) worst = v
  }
  return worst
}

/** Mean severity, rounded and clamped to [0,2]. Null when no data. */
export const meanSeverity = (vals: ReadonlyArray<unknown>): Severity | null => {
  const xs = vals.filter(isSeverity)
  if (!xs.length) return null
  const avg = xs.reduce((a, b) => a + b, 0) / xs.length
  return Math.max(0, Math.min(2, Math.round(avg))) as Severity
}